import { Injector } from '@angular/core';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { FormID } from '@finxone-platform/form-action';
import {
  ACCOUNTTYPE,
  ActionButtonOutcomesType,
  BaseWidgetProperties,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { catchError, of } from 'rxjs';
import { UpdateFormDataActionWithId } from '../../../actions/form-submission.action';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { MetadataService } from '../../../services/metadata-service/metadata-service.service';
import { CheckPayeeVerificationRequest } from '../../../services/metadata-service/metadata-service.type';
import { handleRedirection } from '../cta-redirection.utils';

// Falls back to the onboarding flow's name fields when linkedAccountName wasn't collected on this form.
export function resolveLinkedAccountName(onboardingFormValues: Record<string, string>): string {
  return (
    onboardingFormValues['linkedAccountName'] ||
    [onboardingFormValues['firstName'], onboardingFormValues['middleName'], onboardingFormValues['lastName']]
      .filter(Boolean)
      .join(' ')
  );
}

export function onboardingBankDetailsVerification(
  injector: Injector,
  widgetProperties: BaseWidgetProperties,
): void {
  const store = injector.get(Store);
  const metadataService = injector.get(MetadataService);
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);

  const ctaAction = widgetProperties.buttonActionType as string;
  const onboardingFormValues =
    store.selectSnapshot(FormActionState.getFormActionStateWithId(FormID.ONBOARDING_FLOW))?.formData ?? {};

  const request: CheckPayeeVerificationRequest = {
    name: resolveLinkedAccountName(onboardingFormValues),
    accountIdentifier: {
      accountNumber: onboardingFormValues['linkedAccountNumber'],
      sortCode: onboardingFormValues['linkedAccountSort'],
    },
    accountType: ACCOUNTTYPE.PERSONAL,
    isPrimary: onboardingFormValues['isPrimary'] ?? true,
  };

  ctaButtonSignalService.setSignal({ [ctaAction]: false });

  metadataService
    .checkPayeeVerification(request)
    .pipe(
      catchError((err) => {
        ctaButtonSignalService.setSignal({ [ctaAction]: true });
        handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.FAILURE);
        return of(err);
      }),
    )
    .subscribe((response) => {
      ctaButtonSignalService.setSignal({ [ctaAction]: true });

      if (!response) return;

      /**
       * In case of a successful response, we expect to receive a verification token which can be used in subsequent steps of the onboarding flow. This token is stored in the form data for later use. If the response does not contain the expected token, we treat it as a failure and redirect accordingly.
       */
      if (response?.verificationToken) {
        store.dispatch(
          new UpdateFormDataActionWithId(
            {
              bankDetailsVerificationTokens: [response.verificationToken],
              copVerifiedAt: new Date().toISOString(),
            },
            FormID.ONBOARDING_FLOW,
          ),
        );
        handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.SUCCESS);
      } else {
        handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.FAILURE);
      }
    });
}
